import React, { useMemo, useState } from 'react';
import { Input, Select, Tooltip } from 'antd';
import dayjs from 'dayjs';
import './leadActivityTimeline.css';

// Section names written by Backend/utility/leadHistory.js (TIMELINE_SECTIONS).
const SECTION_ORDER = [
  'Status & Stage', 'Assignment', 'Follow-up', 'Lead Details', 'Contacts', 'Calls',
  'Emails', 'LinkedIn', 'Scheduled Calls', 'OPS Calls', 'Comments', 'Other',
];
const SECTION_ICONS = {
  'Status & Stage': 'fa-flag', Assignment: 'fa-user-tag', 'Follow-up': 'fa-calendar-check',
  'Lead Details': 'fa-building', Contacts: 'fa-address-book', Calls: 'fa-phone', Emails: 'fa-envelope',
  LinkedIn: 'fa-brands fa-linkedin', 'Scheduled Calls': 'fa-calendar-days', 'OPS Calls': 'fa-headset',
  Comments: 'fa-comment-dots', Other: 'fa-circle-info',
};
// Entries written before 2026-11-04 have no `section`; infer it from the field label.
const LEGACY_FIELD_SECTIONS = {
  Status: 'Status & Stage', Stage: 'Status & Stage', 'Pipeline Stage': 'Status & Stage', Priority: 'Status & Stage',
  Channel: 'Status & Stage', Successful: 'Status & Stage', 'Lost Reason': 'Status & Stage', 'Deal Value': 'Status & Stage',
  'Assign To': 'Assignment', 'Assigned To': 'Assignment', Locked: 'Assignment',
  'Follow-up': 'Follow-up', 'Follow-up Date': 'Follow-up',
  Agency: 'Lead Details', DBA: 'Lead Details', Website: 'Lead Details', LinkedIn: 'Lead Details',
  Contacts: 'Contacts', Calls: 'Calls', Emails: 'Emails', 'LinkedIn Activity': 'LinkedIn',
  'Scheduled Calls': 'Scheduled Calls', 'OPS Calls': 'OPS Calls',
};
const SECTION_NOUNS = {
  Contacts: 'contact', Calls: 'call', Emails: 'email', LinkedIn: 'LinkedIn activity',
  'Scheduled Calls': 'scheduled call', 'OPS Calls': 'OPS call',
};
const ACTOR_BADGES = {
  cron: 'Automation', bulkOutreach: 'Bulk Outreach', bulkEdit: 'Bulk Edit', system: 'System', mergeLeads: 'Merge',
};
const ROWS_SHOWN_COLLAPSED = 6;
const ENTRIES_PAGE_SIZE = 30;
const EMPTY = '—';
const ISO_DATE = /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?Z/g;
const AVATAR_COLORS = ['#2563eb', '#7c3aed', '#db2777', '#ea580c', '#0d9488', '#4f46e5', '#65a30d', '#0891b2'];

const sectionOf = (change) => change.section || LEGACY_FIELD_SECTIONS[change.field] || 'Other';

// ISO timestamps are stored time-zone neutral; show them in the viewer's zone.
// Midnight means "a date without a time" (date pickers), so the time is omitted.
const formatIso = (iso, dateOnly) => {
  const d = dayjs(iso);
  if (!d.isValid()) return iso;
  return dateOnly || (d.hour() === 0 && d.minute() === 0) ? d.format('MMM D, YYYY') : d.format('MMM D, YYYY h:mm A');
};
const formatValue = (value, { dateOnly = false } = {}) =>
  (value === undefined || value === null || value === '' ? EMPTY : String(value).replace(ISO_DATE, (iso) => formatIso(iso, dateOnly)));

const relativeTime = (ts) => {
  const mins = Math.floor((Date.now() - ts) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hr${hrs > 1 ? 's' : ''} ago`;
  const days = Math.floor(hrs / 24);
  return days < 7 ? `${days} day${days > 1 ? 's' : ''} ago` : '';
};
const fullTimestamp = (ts) => new Date(ts).toLocaleString(undefined, {
  weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
  hour: 'numeric', minute: '2-digit', second: '2-digit', timeZoneName: 'short',
});
const dayLabel = (ts) => {
  const d = dayjs(ts);
  if (d.isSame(dayjs(), 'day')) return `Today · ${d.format('ddd, MMM D, YYYY')}`;
  if (d.isSame(dayjs().subtract(1, 'day'), 'day')) return `Yesterday · ${d.format('ddd, MMM D, YYYY')}`;
  return d.format('dddd, MMM D, YYYY');
};
const initials = (name) => String(name || '?').trim().split(/\s+/).slice(0, 2).map((p) => p[0]).join('').toUpperCase() || '?';
const avatarColor = (name) => {
  let hash = 0;
  for (const ch of String(name || '')) hash = (hash * 31 + ch.charCodeAt(0)) | 0;
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

// Groups one entry's changes into sections → blocks. A block is either a plain field
// change, or one array item (a call, a contact…) with all of its field changes.
const buildSections = (changes) => {
  const bySection = new Map();
  for (const c of changes) {
    const section = sectionOf(c);
    if (!bySection.has(section)) bySection.set(section, []);
    const blocks = bySection.get(section);
    if (c.kind && c.item !== undefined && c.item !== null) {
      const key = `${c.kind}|${c.item}`;
      let block = blocks.find((b) => b.key === key);
      if (!block) blocks.push(block = { key, kind: c.kind, item: c.item, rows: [] });
      block.rows.push(c);
    } else {
      blocks.push({ key: `${c.field}|${blocks.length}`, rows: [c] });
    }
  }
  return SECTION_ORDER.filter((s) => bySection.has(s)).map((s) => ({ section: s, blocks: bySection.get(s) }));
};

// Older entries carry only an action ("Calls Updated") and/or the lead status at the time.
const GENERIC_LEGACY_VALUES = new Set(['', 'Updated', 'Created']);
const legacyDescription = (entry) => [
  !GENERIC_LEGACY_VALUES.has(entry.action || '') && entry.action,
  !GENERIC_LEGACY_VALUES.has(entry.leadStatus || '') && `Lead status: ${entry.leadStatus}`,
].filter(Boolean).join(' · ');

const entryVerb = (entry) => {
  if (entry.isCreated) return 'created this lead';
  if (entry.action === 'Merge') return 'merged a duplicate into this lead';
  const n = entry.changes.length;
  return n ? `made ${n} change${n > 1 ? 's' : ''}` : 'updated this lead';
};

function Value({ value, tone, dateOnly }) {
  const [expanded, setExpanded] = useState(false);
  const text = formatValue(value, { dateOnly });
  const isEmpty = text === EMPTY;
  const isLong = text.length > 140;
  return (
    <span
      className={`lat-value lat-value--${isEmpty ? 'empty' : tone}${isLong && !expanded ? ' lat-value--clamped' : ''}`}
      title={isLong && !expanded ? 'Click to show all' : undefined}
      onClick={isLong ? () => setExpanded((v) => !v) : undefined}
    >
      {isEmpty ? 'empty' : text}
    </span>
  );
}

function ChangeRow({ change, kind }) {
  const dateOnly = change.field === 'Date';
  if (kind === 'added') {
    return (
      <div className="lat-row">
        <span className="lat-row__field">{change.field}</span>
        <Value value={change.newValue} tone="plain" dateOnly={dateOnly} />
      </div>
    );
  }
  if (kind === 'removed') {
    return (
      <div className="lat-row">
        <span className="lat-row__field">{change.field}</span>
        <Value value={change.oldValue} tone="old" dateOnly={dateOnly} />
      </div>
    );
  }
  return (
    <div className="lat-row">
      <span className="lat-row__field">{change.field}</span>
      <span className="lat-row__diff">
        <Value value={change.oldValue} tone="old" dateOnly={dateOnly} />
        <i className="fa-solid fa-arrow-right lat-row__arrow" aria-label="changed to" />
        <Value value={change.newValue} tone="new" dateOnly={dateOnly} />
      </span>
    </div>
  );
}

const KIND_META = {
  added: { label: 'Added', icon: 'fa-plus' },
  removed: { label: 'Removed', icon: 'fa-minus' },
  changed: { label: 'Edited', icon: 'fa-pen' },
};

function EntryChanges({ changes }) {
  const [expanded, setExpanded] = useState(false);
  const sections = useMemo(() => buildSections(changes), [changes]);

  // Collapse by counting rendered rows across sections.
  let budget = expanded ? Infinity : ROWS_SHOWN_COLLAPSED;
  const totalRows = changes.length;
  const rendered = [];
  for (const { section, blocks } of sections) {
    if (budget <= 0) break;
    const shownBlocks = [];
    for (const block of blocks) {
      if (budget <= 0) break;
      const rows = block.rows.slice(0, budget);
      budget -= rows.length;
      shownBlocks.push({ ...block, rows });
    }
    rendered.push({ section, blocks: shownBlocks });
  }
  const hiddenRows = expanded ? 0 : Math.max(0, totalRows - ROWS_SHOWN_COLLAPSED);

  return (
    <div className="lat-changes">
      {rendered.map(({ section, blocks }) => (
        <div key={section} className="lat-section">
          <div className="lat-section__title">
            <i className={`${SECTION_ICONS[section]?.startsWith('fa-brands') ? '' : 'fa-solid '}${SECTION_ICONS[section] || 'fa-circle-info'}`} />
            {section}
          </div>
          {blocks.map((block) => (block.kind ? (
            <div key={block.key} className={`lat-item lat-item--${block.kind}`}>
              <div className="lat-item__title">
                <span className={`lat-kind lat-kind--${block.kind}`}>
                  <i className={`fa-solid ${KIND_META[block.kind]?.icon || 'fa-pen'}`} />
                  {KIND_META[block.kind]?.label || block.kind} {SECTION_NOUNS[section] || 'item'}
                </span>
                <span className="lat-item__name">{formatValue(block.item, { dateOnly: true })}</span>
              </div>
              {block.rows.map((c, i) => <ChangeRow key={i} change={c} kind={block.kind} />)}
            </div>
          ) : (
            block.rows.map((c, i) => <ChangeRow key={`${block.key}-${i}`} change={c} kind="changed" />)
          )))}
        </div>
      ))}
      {totalRows > ROWS_SHOWN_COLLAPSED && (
        <button type="button" className="lat-toggle" onClick={() => setExpanded((v) => !v)}>
          {expanded ? 'Show less' : `Show ${hiddenRows} more change${hiddenRows > 1 ? 's' : ''}`}
          <i className={`fa-solid fa-chevron-${expanded ? 'up' : 'down'}`} />
        </button>
      )}
    </div>
  );
}

function Entry({ entry }) {
  const badge = ACTOR_BADGES[entry.actorType];
  const ago = relativeTime(entry.timestamp);
  return (
    <div className="lat-entry">
      <div className="lat-entry__avatar" style={{ background: avatarColor(entry.userName) }} aria-hidden>
        {entry.isCreated ? <i className="fa-solid fa-star" /> : initials(entry.userName)}
      </div>
      <div className="lat-entry__body">
        <div className="lat-entry__head">
          <span className="lat-entry__who">
            <b>{entry.userName || 'Unknown user'}</b> {entryVerb(entry)}
            {badge && <span className="lat-badge">{badge}</span>}
          </span>
          <Tooltip title={fullTimestamp(entry.timestamp)} placement="topRight">
            <span className="lat-entry__time">
              {dayjs(entry.timestamp).format('h:mm A')}
              {ago && <span className="lat-entry__ago">{ago}</span>}
            </span>
          </Tooltip>
        </div>
        {entry.changes.length > 0 ? (
          <EntryChanges changes={entry.changes} />
        ) : entry.summary ? (
          <div className="lat-entry__note">{formatValue(entry.summary)}</div>
        ) : !entry.isCreated ? (
          <div className="lat-entry__note lat-entry__note--muted">
            {legacyDescription(entry) || "Details weren't recorded for this older entry."}
          </div>
        ) : null}
      </div>
    </div>
  );
}

/**
 * Lead Activity Timeline: who changed what, from what to what, and when.
 * `history` = LeadWorkHistory entries ({ changeTime, userName, actorType, action,
 * summary, changes: [{ field, oldValue, newValue, section?, kind?, item? }] }).
 */
export default function LeadActivityTimeline({ history, createdDate, createdBy }) {
  const [section, setSection] = useState('All');
  const [person, setPerson] = useState();
  const [query, setQuery] = useState('');
  const [visible, setVisible] = useState(ENTRIES_PAGE_SIZE);

  const entries = useMemo(() => {
    const list = (history || []).map((row, i) => ({
      key: row?.id || `h-${i}`,
      timestamp: parseInt(row?.changeTime, 10) || 0,
      userName: row?.userName,
      actorType: row?.actorType,
      action: row?.action,
      leadStatus: row?.leadStatus,
      summary: row?.summary,
      changes: Array.isArray(row?.changes) ? row.changes : [],
    }));
    // createdDate arrives as epoch-ms text; parseInt on an ISO string would give the year.
    // dayjs(undefined) is "now", so a missing date must short-circuit.
    const createdTs = !createdDate ? 0
      : /^\d+$/.test(String(createdDate)) ? parseInt(createdDate, 10)
        : (dayjs(createdDate).isValid() ? dayjs(createdDate).valueOf() : 0);
    if (createdTs) {
      list.push({ key: 'created', timestamp: createdTs, userName: createdBy, isCreated: true, changes: [] });
    }
    return list.sort((a, b) => b.timestamp - a.timestamp);
  }, [history, createdDate, createdBy]);

  const sectionCounts = useMemo(() => {
    const counts = {};
    for (const e of entries) {
      for (const s of new Set(e.changes.map(sectionOf))) counts[s] = (counts[s] || 0) + 1;
    }
    return counts;
  }, [entries]);
  const people = useMemo(() => [...new Set(entries.map((e) => e.userName).filter(Boolean))].sort(), [entries]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return entries
      .filter((e) => !person || e.userName === person)
      .map((e) => (section === 'All' ? e : { ...e, changes: e.changes.filter((c) => sectionOf(c) === section) }))
      .filter((e) => section === 'All' || e.changes.length > 0)
      .filter((e) => !q || [e.userName, e.summary, ...e.changes.flatMap((c) => [c.field, c.item, formatValue(c.oldValue), formatValue(c.newValue)])]
        .some((t) => String(t || '').toLowerCase().includes(q)));
  }, [entries, section, person, query]);

  const days = useMemo(() => {
    const groups = [];
    for (const e of filtered.slice(0, visible)) {
      const label = dayLabel(e.timestamp);
      if (groups[groups.length - 1]?.label !== label) groups.push({ label, entries: [] });
      groups[groups.length - 1].entries.push(e);
    }
    return groups;
  }, [filtered, visible]);

  const isFiltered = section !== 'All' || person || query.trim();

  return (
    <div className="lat">
      <div className="lat-toolbar">
        <div className="lat-chips" role="tablist" aria-label="Filter activity by area">
          {['All', ...SECTION_ORDER.filter((s) => sectionCounts[s])].map((s) => (
            <button
              key={s}
              type="button"
              role="tab"
              aria-selected={section === s}
              className={`lat-chip${section === s ? ' lat-chip--active' : ''}`}
              onClick={() => { setSection(s); setVisible(ENTRIES_PAGE_SIZE); }}
            >
              {s}{s !== 'All' && <span className="lat-chip__count">{sectionCounts[s]}</span>}
            </button>
          ))}
        </div>
        <div className="lat-toolbar__filters">
          {people.length > 1 && (
            <Select
              size="small"
              allowClear
              placeholder="Anyone"
              value={person}
              onChange={(v) => { setPerson(v); setVisible(ENTRIES_PAGE_SIZE); }}
              options={people.map((p) => ({ label: p, value: p }))}
              className="lat-person"
              popupMatchSelectWidth={false}
            />
          )}
          <Input
            size="small"
            allowClear
            placeholder="Search changes"
            prefix={<i className="fa-solid fa-magnifying-glass lat-muted" />}
            value={query}
            onChange={(e) => { setQuery(e.target.value); setVisible(ENTRIES_PAGE_SIZE); }}
            className="lat-search"
          />
        </div>
      </div>

      <div className="lat-scroll">
        {days.length === 0 ? (
          <div className="lat-empty">
            {isFiltered ? 'No activity matches these filters.' : 'No activity yet.'}
          </div>
        ) : (
          days.map((day) => (
            <div key={day.label} className="lat-day">
              <div className="lat-day__label">{day.label}</div>
              {day.entries.map((e) => <Entry key={e.key} entry={e} />)}
            </div>
          ))
        )}
        {filtered.length > visible && (
          <button type="button" className="lat-toggle lat-toggle--older" onClick={() => setVisible((v) => v + ENTRIES_PAGE_SIZE)}>
            Show older activity ({filtered.length - visible} more)
          </button>
        )}
      </div>
    </div>
  );
}
